import { DocumentNode, print } from 'graphql';
import { API_URL } from './constants';
import { getSdk } from './generated/graphql';
import { getSessionStorage } from './sessions';

export interface QueryOptions {
  request: Request;
}

export interface GraphqlResponse<Response> {
  errors: any[];
  data: Response;
}

export type WithHeaders<T> = T & { _headers: Headers };

const AUTH_TOKEN_SESSION_KEY = 'authToken';

async function sendQuery<Response, Variables = {}>(options: {
  query: string;
  variables?: Variables;
  headers?: Headers;
  request?: Request;
}): Promise<GraphqlResponse<Response> & { headers: Headers }> {
  const headers = new Headers(options.headers);
  headers.set('Content-Type', 'application/json');

  let token: string | undefined;
  if (options.request) {
    const cookieHeader = options.request.headers.get('Cookie');
    if (cookieHeader) {
      const match = cookieHeader.match(/vendure-auth-token=([^;]+)/);
      if (match && match[1]) {
        token = match[1];
      }
    }
  }

  if (!token) {
    const session = await getSessionStorage().then((sessionStorage) =>
      sessionStorage.getSession(options.request?.headers.get('Cookie')),
    );
    if (session) {
      token = session.get(AUTH_TOKEN_SESSION_KEY);
    }
  }

  if (token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  return fetch(API_URL, {
    method: 'POST',
    body: JSON.stringify({
      query: options.query,
      variables: options.variables,
    }),
    headers,
  }).then(async (res) => {
    const contentType = res.headers.get('content-type');
    if (res.ok && contentType && contentType.includes('application/json')) {
      return {
        ...(await res.json()),
        headers: res.headers,
      };
    }
    const body = await res.text();
    console.error(
      `GraphQL fetch failed. Status: ${res.status}. Content-Type: ${contentType}. URL: ${API_URL}`,
    );
    throw new Error(`GraphQL fetch failed: ${res.status} ${res.statusText}`);
  });
}

const baseSdk = getSdk<QueryOptions, unknown>(requester);

type Sdk = typeof baseSdk;
type SdkWithHeaders = {
  [k in keyof Sdk]: (
    ...args: Parameters<Sdk[k]>
  ) => Promise<Awaited<ReturnType<Sdk[k]>> & { _headers: Headers }>;
};

export const sdk: SdkWithHeaders = baseSdk as any;

export function requester<R, V>(
  doc: DocumentNode,
  vars?: V,
  options?: { headers?: Headers; request?: Request },
): Promise<R & { _headers: Headers }> {
  return sendQuery<R, V>({
    query: print(doc),
    variables: vars,
    ...options,
  }).then(async (response) => {
    const token = response.headers.get('vendure-auth-token');
    const headers: Record<string, string> = {};
    if (token) {
      // If Vendure responded with an auth token, it means a new Vendure session
      // has started. In this case, we will store that auth token in the Remix session
      // so that we can attach it as an Authorization header in all subsequent requests.
      const sessionStorage = await getSessionStorage();
      const session = await sessionStorage.getSession(
        options?.request?.headers.get('Cookie'),
      );
      if (session) {
        session.set(AUTH_TOKEN_SESSION_KEY, token);
        headers['Set-Cookie'] = await sessionStorage.commitSession(session);
      }
    }
    headers['x-vendure-api-url'] = API_URL;
    if (response.errors) {
      // For schema validation errors, return the partial data if available
      if (
        response.errors.some((err) =>
          err.message.includes('Cannot return null for non-nullable field'),
        )
      ) {
        return {
          ...response.data,
          _headers: new Headers(headers),
          _errors: response.errors,
        };
      }
      throw new Error(JSON.stringify(response.errors[0]));
    }
    return { ...response.data, _headers: new Headers(headers) };
  });
}
